import React from "react";
import { ModeRendererProps } from "./types";

// VASARELY × ASTIER DE VILLATTE: Handmade ceramics meet digital symmetry
// Crackled glazes, white relief grids, matte reflections
export function VasarelyAstierMode({ brand }: ModeRendererProps) {
  return (
    <g>
      <defs>
        <linearGradient id="astier-white1" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#FFFFFF" />
          <stop offset="50%" stopColor="#F8F8F6" />
          <stop offset="100%" stopColor="#F0F0E8" />
        </linearGradient>
        <linearGradient id="astier-white2" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#F5F5F0" />
          <stop offset="100%" stopColor="#E8E8E0" />
        </linearGradient>
        <pattern id="astier-crackle" x="0" y="0" width="20" height="20" patternUnits="userSpaceOnUse">
          <path d="M 0,10 L 5,5 L 10,10 L 15,5 L 20,10" fill="none" stroke="#D8D8D0" strokeWidth="0.5" opacity="0.4" />
          <path d="M 10,0 L 5,5 L 10,10 L 5,15 L 10,20" fill="none" stroke="#D8D8D0" strokeWidth="0.5" opacity="0.4" />
        </pattern>
        <filter id="astier-matte">
          <feGaussianBlur in="SourceGraphic" stdDeviation="0.5" />
        </filter>
      </defs>

      {/* Matte white background */}
      <rect x="0" y="0" width="794" height="1123" fill="url(#astier-white1)" />
      <rect x="0" y="0" width="794" height="1123" fill="url(#astier-crackle)" />

      <g transform="translate(397 540)">
        {/* White relief grids */}
        {Array.from({ length: 12 }).map((_, i) =>
          Array.from({ length: 16 }).map((_, j) => {
            const x = (i - 5.5) * 62;
            const y = (j - 7.5) * 65;
            
            return (
              <g key={`${i}-${j}`}>
                <rect
                  x={x - 28}
                  y={y - 30}
                  width="56"
                  height="60"
                  fill="url(#astier-white2)"
                  stroke="#E0E0D8"
                  strokeWidth="1"
                  opacity="0.4"
                  filter="url(#astier-matte)"
                />
                <rect
                  x={x - 26}
                  y={y - 28}
                  width="52"
                  height="56"
                  fill="none"
                  stroke="#F0F0E8"
                  strokeWidth="2"
                  opacity="0.6"
                />
              </g>
            );
          })
        )}

        {/* Ceramic circles (handmade imperfection) */}
        {Array.from({ length: 12 }).map((_, i) => {
          const r = 50 + i * 24;
          const irregularity = Math.sin(i * 0.7) * 3;
          
          return (
            <circle
              key={i}
              cx={irregularity}
              cy={irregularity * 0.5}
              r={r}
              fill="none"
              stroke="#D8D8D0"
              strokeWidth="2"
              opacity={0.35 - i * 0.025}
              filter="url(#astier-matte)"
            />
          );
        })}

        {/* Matte reflections (subtle gradients) */}
        {[
          { x: -150, y: -200, size: 120, angle: 5 },
          { x: 50, y: -180, size: 100, angle: -8 },
          { x: -130, y: 30, size: 110, angle: 3 },
          { x: 70, y: 50, size: 130, angle: -5 },
        ].map((plate, i) => (
          <g key={i} transform={`translate(${plate.x} ${plate.y}) rotate(${plate.angle})`}>
            <ellipse
              cx="0"
              cy="0"
              rx={plate.size / 2}
              ry={plate.size / 2.2}
              fill="url(#astier-white2)"
              opacity="0.5"
              filter="url(#astier-matte)"
            />
            <ellipse
              cx="0"
              cy="0"
              rx={plate.size / 2}
              ry={plate.size / 2.2}
              fill="none"
              stroke="#E0E0D8"
              strokeWidth="2"
              opacity="0.4"
            />
            <ellipse
              cx="0"
              cy="0"
              rx={plate.size / 2.5}
              ry={plate.size / 2.7}
              fill="none"
              stroke="#F0F0E8"
              strokeWidth="1"
              opacity="0.6"
            />
          </g>
        ))}

        {/* Digital symmetry structure */}
        {Array.from({ length: 8 }).map((_, i) => {
          const angle = (i * Math.PI) / 4;
          return (
            <line
              key={i}
              x1="0"
              y1="0"
              x2={Math.cos(angle) * 260}
              y2={Math.sin(angle) * 260}
              stroke="#E0E0D8"
              strokeWidth="1"
              opacity="0.2"
            />
          );
        })}

        {/* Central ceramic piece */}
        <circle cx="0" cy="0" r="100" fill="url(#astier-white1)" filter="url(#astier-matte)" opacity="0.8" />
        <circle cx="0" cy="0" r="100" fill="none" stroke="#D8D8D0" strokeWidth="3" opacity="0.5" />
        <circle cx="0" cy="0" r="90" fill="none" stroke="#E8E8E0" strokeWidth="2" opacity="0.4" />
        <circle cx="0" cy="0" r="80" fill="none" stroke="#F0F0E8" strokeWidth="1" opacity="0.6" />

        {/* Crackle pattern on center */}
        {Array.from({ length: 12 }).map((_, i) => {
          const angle = (i * Math.PI) / 6;
          const r1 = 50;
          const r2 = 80;
          
          return (
            <line
              key={i}
              x1={Math.cos(angle) * r1}
              y1={Math.sin(angle) * r1}
              x2={Math.cos(angle) * r2}
              y2={Math.sin(angle) * r2}
              stroke="#D8D8D0"
              strokeWidth="0.5"
              opacity="0.4"
            />
          );
        })}
      </g>

      {/* Craft-tech */}
      <text x="397" y="1010" textAnchor="middle" fontFamily="serif" fontSize="10" fill="#C0C0B8" fontStyle="italic" letterSpacing="2">
        imperfection structurée
      </text>
    </g>
  );
}
